import { create } from 'zustand';
import { adminCan, type AdminLevel, type AdminPerm, type AdminRole } from '@dd/shared';
import { api, setCsrf } from './api';

export interface Admin { id: string; name: string; email: string; role: AdminRole; canReview?: boolean; perms?: Partial<Record<AdminPerm, AdminLevel>> }
/** What this person may do: the server sends the effective map (role preset + the Super Admin's switches). */
export const adminMay = (a: Admin | null | undefined, perm: AdminPerm): boolean => !!a && (a.perms ? (a.perms[perm] ?? 0) !== 0 : adminCan(a.role, perm));
interface S {
  phase: 'boot' | 'out' | 'in';
  admin: Admin | null;
  boot: () => Promise<void>;
  signedIn: (a: Admin, csrf: string) => void;
  logout: () => Promise<void>;
  expire: () => void;
}
export const useAuth = create<S>((set, get) => ({
  phase: 'boot', admin: null,
  async boot() {
    try { const r = await api.get<{ admin: Admin; csrfToken: string }>('/auth/me'); setCsrf(r.csrfToken); set({ admin: r.admin, phase: 'in' }); }
    catch { set({ admin: null, phase: 'out' }); }
  },
  signedIn(a, csrf) { setCsrf(csrf); set({ admin: a, phase: 'in' }); },
  async logout() { try { await api.post('/auth/logout'); } catch { /* ignore */ } setCsrf(null); set({ admin: null, phase: 'out' }); },
  expire() { if (get().phase === 'in') { setCsrf(null); set({ admin: null, phase: 'out' }); } },
}));
export const useCan = (perm: AdminPerm): boolean => useAuth((s) => adminMay(s.admin, perm));
/** a stable checker for use inside render: const can = useMay(); can('suspend') */
export const useMay = () => { const a = useAuth((s) => s.admin); return (perm: AdminPerm) => adminMay(a, perm); };
export const useRole = (): AdminRole | null => useAuth((s) => s.admin?.role ?? null);
